import jsPDF from 'jspdf';
import { doc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';

/**
 * Existing Conditions assessment → PDF in the lead's document collection.
 *
 * The lead answers the assessment in /start/existing-conditions and the answers
 * land on accounts/{id} as businessAssessment / salesData / operationsData /
 * financeData / marketingData / techData. Internally we want the same thing as a
 * readable document, so this renders those fields and files the result under the
 * account's `customDocuments` (General department, internal only) — the same
 * array the CRM collection tab reads.
 *
 * Storage path and document id are stable, so re-completing the assessment
 * replaces the file and the row instead of stacking duplicates.
 */

const DOCUMENT_ID = 'existing-conditions-assessment';
const DOCUMENT_NAME = 'Existing Conditions Assessment';
const STORAGE_FOLDER = 'lead-documents';

const RED = [220, 38, 38];
const INK = [17, 24, 39];
const MUTED = [107, 114, 128];
const RULE = [229, 231, 235];

const isBlank = (v) =>
  v === null ||
  v === undefined ||
  v === '' ||
  (Array.isArray(v) && v.length === 0) ||
  (typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0);

const toNumber = (v) => {
  const n = Number(String(v ?? '').replace(/[$,\s%]/g, ''));
  return Number.isFinite(n) ? n : null;
};

const formatMoney = (v) => {
  const n = toNumber(v);
  if (n === null) return String(v);
  return `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
};

const formatPercent = (v) => {
  const n = toNumber(v);
  if (n === null) return String(v);
  return `${n}%`;
};

const formatYesNo = (v) => {
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  const s = String(v).trim().toLowerCase();
  if (s === 'true' || s === 'yes') return 'Yes';
  if (s === 'false' || s === 'no') return 'No';
  return String(v);
};

const formatValue = (value, format) => {
  if (Array.isArray(value)) return value.map((v) => formatValue(v, format)).join(', ');
  if (typeof value === 'boolean') return formatYesNo(value);
  switch (format) {
    case 'money':
      return formatMoney(value);
    case 'percent':
      return formatPercent(value);
    case 'rating':
      return `${value} / 10`;
    case 'yesno':
      return formatYesNo(value);
    default:
      return String(value);
  }
};

/** A label/value line. Blank values are dropped so the PDF only shows what they answered. */
const row = (label, value, format) =>
  isBlank(value) ? null : { type: 'row', label, value: formatValue(value, format) };

/** A wrapped paragraph (mission, vision, agency description…). */
const paragraph = (label, value) =>
  isBlank(value) ? null : { type: 'paragraph', label, value: String(value) };

/** A bulleted list (core values, ad platforms, custom goals…). */
const list = (label, items) =>
  !Array.isArray(items) || items.length === 0 ? null : { type: 'list', label, items: items.map(String) };

/** Small table for the org chart. */
const table = (label, columns, rows) =>
  !Array.isArray(rows) || rows.length === 0 ? null : { type: 'table', label, columns, rows };

/** A year-over-year block (revenue, GPM, NPM, leads) rendered as three lines. */
const trend = (label, data, format) => {
  if (!data || typeof data !== 'object') return null;
  const entries = [
    ['2 years ago', data.twoYearsAgo],
    ['1 year ago', data.oneYearAgo],
    ['Current year projection', data.currentYearProjection],
    ['Next year goal', data.nextYearGoal],
  ].filter(([, v]) => !isBlank(v));
  if (entries.length === 0) return null;
  return {
    type: 'list',
    label,
    items: entries.map(([name, value]) => `${name}: ${formatValue(value, format)}`),
  };
};

const customGoalItems = (goals) =>
  (Array.isArray(goals) ? goals : [])
    .filter((g) => g && (g.name || g.target))
    .map((g) => `${g.name || 'Goal'}: ${g.target || '—'}`);

const compact = (blocks) => blocks.filter(Boolean);

const section = (title, blocks) => {
  const items = compact(blocks);
  return items.length === 0 ? null : { title, blocks: items };
};

/**
 * Turn an account document into the printable sections.
 * Exported for reuse/verification; the renderer below consumes the same shape.
 */
export function buildAssessmentSections(account = {}) {
  const business = account.businessAssessment || {};
  const sales = account.salesData || {};
  const operations = account.operationsData || {};
  const finance = account.financeData || {};
  const marketing = account.marketingData || {};
  const tech = account.techData || {};

  return compact([
    section('Business Overview', [
      row('Contractor type', business.contractorType),
      row('Project type', business.projectType),
      row('Project focus', business.projectFocusType),
      row('Client types', business.clientTypes),
      paragraph('Mission', business.mission),
      paragraph('Vision', business.vision),
      list('Core values', business.values),
    ]),

    section('Sales', [
      row('Sales rating', sales.salesRating ?? business.salesRating, 'rating'),
      row('Close ratio', sales.closeRatio ?? business.closeRatio, 'percent'),
      row('Referral business', sales.referralBusinessPercentage ?? business.referralBusinessPercentage, 'percent'),
      row('Repeat customers', sales.repeatCustomerPercentage, 'percent'),
      row('Salespeople', sales.salesmanCount),
      trend('Revenue', sales.revenueData || business.revenueData, 'money'),
      row('Annual revenue target', sales.goalsData?.annualRevenueTarget, 'money'),
      row('Closing rate target', sales.goalsData?.closingRateTarget, 'percent'),
      list('Additional sales goals', customGoalItems(sales.goalsData?.customGoals)),
    ]),

    section('Operations', [
      row('Operations rating', operations.operationsRating ?? business.operationsRating, 'rating'),
      row('Team size', operations.teamSize),
      row('Team grade', operations.teamGrade),
      row('Culture grade', operations.cultureGrade ?? business.cultureGrade),
      row('Culture rating', operations.companyCultureRating, 'rating'),
      row('Has SOPs', operations.hasSOPs, 'yesno'),
      row('Has job descriptions', operations.hasJobDescriptions, 'yesno'),
      row('Has KPIs', operations.hasKPIs, 'yesno'),
      trend('Gross profit margin', operations.gpmData, 'percent'),
      row('Gross profit margin target', operations.goalsData?.grossProfitMarginTarget, 'percent'),
      row('Slippage target', operations.goalsData?.slippagePercentageTarget, 'percent'),
      list('Additional operations goals', customGoalItems(operations.goalsData?.customGoals)),
      table(
        'Org chart',
        ['Name', 'Title', 'Reports to', 'Tenure', 'Grade'],
        (operations.orgChartData?.employees || []).map((e) => [
          e?.name || '—',
          e?.title || '—',
          e?.reportsTo || '—',
          e?.tenure || '—',
          e?.grade || '—',
        ])
      ),
    ]),

    section('Finance', [
      row('Finance rating', finance.financeRating ?? business.financeRating, 'rating'),
      row('Knows overhead', finance.knowsOverhead ?? business.knowsOverhead, 'yesno'),
      row('Knows labor burden', finance.knowsLaborBurden ?? business.knowsLaborBurden, 'yesno'),
      row('Knows project runway', finance.knowsProjectRunway ?? business.knowsProjectRunway, 'yesno'),
      row('Monthly reconciliation', finance.hasMonthlyReconciliation, 'yesno'),
      row('Cash flow projection', finance.hasCashFlowProjection, 'yesno'),
      row('Budgets vs. actuals', finance.hasProjectBudgetsVsActuals, 'yesno'),
      row('Carries debt', finance.hasDebt, 'yesno'),
      row('Financial partners', finance.hasFinancialPartners, 'yesno'),
      row('Line of credit', finance.hasCreditLine, 'yesno'),
      row('Unencumbered cash', finance.unencumberedCash, 'money'),
      row('Gross profit margin', finance.grossProfitMargin, 'percent'),
      row('Net profit margin', finance.netProfitMargin, 'percent'),
      trend('Net profit margin history', finance.npmData, 'percent'),
      row('Monthly overhead target', finance.goalsData?.monthlyOverheadTarget, 'money'),
      row('Net profit margin target', finance.goalsData?.netProfitMarginTarget, 'percent'),
      list('Additional finance goals', customGoalItems(finance.goalsData?.customGoals)),
      list(
        'Financial statements provided',
        compact([
          finance.incomeStatementFileName || (finance.incomeStatementUrl ? 'Income statement' : null),
          finance.balanceSheetFileName || (finance.balanceSheetUrl ? 'Balance sheet' : null),
          finance.cashFlowStatementFileName || (finance.cashFlowStatementUrl ? 'Cash flow statement' : null),
        ])
      ),
    ]),

    section('Marketing', [
      row('Marketing rating', marketing.marketingRating ?? business.marketingRating, 'rating'),
      row('Monthly marketing budget', marketing.monthlyMarketingBudget, 'money'),
      row('Works with an agency', marketing.workingWithMarketingAgency, 'yesno'),
      row('Agency type', marketing.marketingAgencyType ?? business.marketingAgencyType),
      paragraph('Agency description', marketing.marketingAgencyDescription),
      row('Running digital ads', marketing.runningDigitalAds, 'yesno'),
      row('Ad platforms', marketing.digitalAdPlatforms),
      row('Website managed by', marketing.websiteManager),
      row('Engages social media', marketing.engagesSocialMedia ?? business.engagesSocialMedia, 'yesno'),
      row('Engages paid ads', marketing.engagesPaidAds ?? business.engagesPaidAds, 'yesno'),
      row('Sends a newsletter', marketing.engagesNewsletter ?? business.engagesNewsletter, 'yesno'),
      trend('Leads', marketing.leadsData),
      row('Monthly leads target', marketing.goalsData?.monthlyLeadsTarget),
      row('Cost per lead target', marketing.goalsData?.costPerLeadTarget, 'money'),
      list('Additional marketing goals', customGoalItems(marketing.goalsData?.customGoals)),
    ]),

    section('Technology', [
      row('Technology rating', tech.techRating ?? business.techRating, 'rating'),
      row('CRM', tech.whatCRM || tech.techStackData?.crm),
      row('CRM optimization', tech.crmOptimization, 'rating'),
      row('Has automations', tech.hasAutomations, 'yesno'),
      row('Uses AI tools', tech.hasAITools, 'yesno'),
      row('Has website plugins', tech.hasWebsitePlugins, 'yesno'),
      row('Active automations target', tech.goalsData?.activeAutomationsTarget),
      row('Tech stack monthly cost target', tech.goalsData?.techStackMonthlyCostsTarget, 'money'),
      list('Additional technology goals', customGoalItems(tech.goalsData?.customGoals)),
    ]),
  ]);
}

/** Load the BBA mark as a data URL so jsPDF can embed it; never blocks the PDF. */
const loadLogo = async () => {
  try {
    const response = await fetch('/bba-logo.png');
    if (!response.ok) return null;
    const blob = await response.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

/**
 * Render the sections to a PDF blob. Text is drawn with jsPDF directly (no
 * html2canvas) so the output stays selectable, searchable and small.
 */
async function renderAssessmentPdf({ companyName, contactName, contactEmail, completedAt, sections }) {
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 16;
  const contentWidth = pageWidth - margin * 2;
  const labelWidth = 62;
  const bottomLimit = pageHeight - 20;

  const logo = await Promise.race([loadLogo(), new Promise((r) => setTimeout(() => r(null), 3000))]);

  let y = margin;

  const newPage = () => {
    pdf.addPage();
    y = margin;
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(...MUTED);
    pdf.text(`${DOCUMENT_NAME}${companyName ? ` — ${companyName}` : ''}`, margin, y);
    y += 6;
  };

  const ensureSpace = (needed) => {
    if (y + needed > bottomLimit) newPage();
  };

  // Cover block
  if (logo) {
    const logoWidth = 46;
    const logoHeight = logoWidth / 4; // bba-logo.png is a wide wordmark
    try {
      pdf.addImage(logo, 'PNG', (pageWidth - logoWidth) / 2, y, logoWidth, logoHeight);
      y += logoHeight + 8;
    } catch (err) {
      console.error('Assessment PDF: could not embed logo', err);
    }
  }

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(20);
  pdf.setTextColor(...INK);
  pdf.text(DOCUMENT_NAME, pageWidth / 2, y, { align: 'center' });
  y += 8;

  if (companyName) {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(13);
    pdf.text(companyName, pageWidth / 2, y, { align: 'center' });
    y += 7;
  }

  pdf.setFontSize(9);
  pdf.setTextColor(...MUTED);
  const metaLine = compact([
    contactName,
    contactEmail,
    `Completed ${new Date(completedAt).toLocaleDateString()}`,
  ]).join('  ·  ');
  pdf.text(metaLine, pageWidth / 2, y, { align: 'center' });
  y += 10;

  const drawSectionHeader = (title) => {
    ensureSpace(16);
    pdf.setFillColor(...RED);
    pdf.rect(margin, y, contentWidth, 8, 'F');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(11);
    pdf.setTextColor(255, 255, 255);
    pdf.text(title, margin + 3, y + 5.6);
    y += 12;
  };

  const drawRow = (label, value) => {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    const valueLines = pdf.splitTextToSize(value, contentWidth - labelWidth);
    const height = Math.max(valueLines.length * 5, 5) + 2.5;
    ensureSpace(height);
    pdf.setTextColor(...MUTED);
    pdf.text(pdf.splitTextToSize(label, labelWidth - 4), margin, y);
    pdf.setTextColor(...INK);
    pdf.text(valueLines, margin + labelWidth, y);
    y += height;
    pdf.setDrawColor(...RULE);
    pdf.line(margin, y - 1.5, pageWidth - margin, y - 1.5);
  };

  const drawParagraph = (label, value) => {
    pdf.setFontSize(10);
    const lines = pdf.splitTextToSize(value, contentWidth);
    ensureSpace(lines.length * 5 + 9);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...MUTED);
    pdf.text(label, margin, y);
    y += 5;
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(...INK);
    pdf.text(lines, margin, y);
    y += lines.length * 5 + 4;
  };

  const drawList = (label, items) => {
    pdf.setFontSize(10);
    ensureSpace(11);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...MUTED);
    pdf.text(label, margin, y);
    y += 5;
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(...INK);
    items.forEach((item) => {
      const lines = pdf.splitTextToSize(item, contentWidth - 6);
      ensureSpace(lines.length * 5);
      pdf.text('•', margin + 1, y);
      pdf.text(lines, margin + 6, y);
      y += lines.length * 5;
    });
    y += 4;
  };

  const drawTable = (label, columns, rows) => {
    pdf.setFontSize(9);
    ensureSpace(16);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...MUTED);
    pdf.text(label, margin, y);
    y += 5;

    const colWidth = contentWidth / columns.length;
    const drawHeader = () => {
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(...MUTED);
      columns.forEach((col, i) => pdf.text(col, margin + i * colWidth, y));
      y += 4;
      pdf.setDrawColor(...RULE);
      pdf.line(margin, y - 1.5, pageWidth - margin, y - 1.5);
    };
    drawHeader();

    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(...INK);
    rows.forEach((cells) => {
      const wrapped = cells.map((cell) => pdf.splitTextToSize(String(cell), colWidth - 3));
      const height = Math.max(...wrapped.map((w) => w.length)) * 4.5 + 2;
      if (y + height > bottomLimit) {
        newPage();
        pdf.setFontSize(9);
        drawHeader();
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(...INK);
      }
      wrapped.forEach((linesForCell, i) => pdf.text(linesForCell, margin + i * colWidth, y));
      y += height;
      pdf.setDrawColor(...RULE);
      pdf.line(margin, y - 1.5, pageWidth - margin, y - 1.5);
    });
    y += 4;
  };

  sections.forEach((sec) => {
    drawSectionHeader(sec.title);
    sec.blocks.forEach((block) => {
      if (block.type === 'row') drawRow(block.label, block.value);
      else if (block.type === 'paragraph') drawParagraph(block.label, block.value);
      else if (block.type === 'list') drawList(block.label, block.items);
      else if (block.type === 'table') drawTable(block.label, block.columns, block.rows);
    });
    y += 4;
  });

  // Footers last, once the page count is known.
  const totalPages = pdf.getNumberOfPages();
  const footerDate = new Date(completedAt).toLocaleDateString();
  for (let page = 1; page <= totalPages; page += 1) {
    pdf.setPage(page);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(...MUTED);
    if (companyName) pdf.text(companyName, margin, pageHeight - 10);
    pdf.text(footerDate, pageWidth / 2, pageHeight - 10, { align: 'center' });
    pdf.text(`Page ${page} of ${totalPages}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
  }

  return pdf.output('blob');
}

/**
 * Build the assessment PDF for a lead, upload it, and file it in the account's
 * document collection under General / internal only.
 *
 * Safe to call repeatedly: the storage path and the `customDocuments` entry id
 * are fixed, so a re-completed assessment replaces the previous PDF and row.
 *
 * @param {object} opts
 * @param {string} opts.accountId - accounts/{id} the assessment belongs to
 * @param {object} opts.account - the account data as saved (post-assessment)
 * @returns {Promise<object|null>} the document entry that was filed, or null on failure
 */
export async function generateAndFileAssessmentPdf({ accountId, account = {} }) {
  if (!accountId) return null;

  try {
    const sections = buildAssessmentSections(account);
    if (sections.length === 0) {
      console.warn('Assessment PDF: no assessment data to render for', accountId);
      return null;
    }

    const contactName =
      account.contact?.name ||
      compact([account.contact?.firstName, account.contact?.lastName]).join(' ') ||
      account.name ||
      '';
    const completedAt =
      account.substatusDates?.['Assessment Done'] || account.lastUpdated || new Date().toISOString();

    const blob = await renderAssessmentPdf({
      companyName: account.company || account.companyName || '',
      contactName,
      contactEmail: account.contact?.email || account.email || '',
      completedAt,
      sections,
    });

    const fileName = `lead-${accountId}-${DOCUMENT_ID}.pdf`;
    const storagePath = `${STORAGE_FOLDER}/${fileName}`;
    const snapshot = await uploadBytes(ref(storage, storagePath), blob, { contentType: 'application/pdf' });
    const url = await getDownloadURL(snapshot.ref);

    const existing = Array.isArray(account.customDocuments) ? account.customDocuments : [];
    const previous = existing.find((d) => d?.id === DOCUMENT_ID);
    const entry = {
      id: DOCUMENT_ID,
      name: DOCUMENT_NAME,
      url,
      uploader: contactName || account.contact?.email || 'Assessment',
      uploadDate: new Date().toISOString(),
      fileName,
      storagePath,
      visibility: 'internal',
      department: 'General',
      folderId: previous?.folderId || '',
      source: 'existing-conditions-assessment',
    };

    const customDocuments = previous
      ? existing.map((d) => (d?.id === DOCUMENT_ID ? entry : d))
      : [...existing, entry];

    await updateDoc(doc(db, 'accounts', accountId), { customDocuments });

    return entry;
  } catch (error) {
    console.error('Assessment PDF: failed to generate or file document', error);
    return null;
  }
}
